import type { TaskFlowRequest } from '@/lib/task-flow/taskFlowStore'

export type CreateContentDirection = {
  contentGoal:
    | 'viral'
    | 'product'
    | 'storytelling'
    | 'ugc'
    | 'auto'
    | ''

  publishTarget:
    | 'shorts'
    | 'feed'
    | 'youtube'
    | 'auto'
    | ''

  durationMode:
    | 'auto'
    | 'short'
    | 'medium'
    | 'long'
    | 'documentary'
    | 'custom'
    | ''

  durationTargetSec?: number

  productionMode?:
    | 'express'
    | 'cinematic_longform'

  visualOutputMode?:
    | 'animated_scenes'
    | 'static_images'
    | 'mixed'

  useCharacter:
    | 'yes'
    | 'no'
    | 'auto'
    | ''

  characterType:
    | 'expert'
    | 'creator'
    | 'founder'
    | 'customer'
    | 'narrator'
    | 'avatar'
    | 'none'
    | 'auto'
    | ''

  hasProductImage:
    | 'yes'
    | 'no'
    | ''
}

export type CreateContentDiscovery = {
  topic: string
  objective:
    | 'vistas'
    | 'leads'
    | 'ventas'
    | 'autoridad'
    | 'comunidad'
    | ''
  audience: string
  desiredAction: string
  mustInclude: string
  mustAvoid: string
}

export type CreateContentDirectionStep =
  | 'content_goal'
  | 'publish_target'
  | 'duration'
  | 'product_image'
  | 'character_presence'
  | 'character_type'
  | 'discovery_topic'
  | 'discovery_objective'
  | 'discovery_audience'
  | 'discovery_action'
  | 'completed'

export type CreateContentDirectionState = {
  request: TaskFlowRequest
  direction: CreateContentDirection
  discovery: CreateContentDiscovery
  step: CreateContentDirectionStep
  snapshotId: number | null
}

export type CreateContentDirectionButton = {
  label: string
  value: string
  emoji?: string
}

export type CreateContentDirectionQuestion = {
  content: string
  buttons: CreateContentDirectionButton[]
}

type DurationOption = {
  id:
    | 'auto'
    | 'short'
    | 'medium'
    | 'long'
    | 'documentary'

  label: string
  hint: string
  durationTargetSec: number
  productionMode:
    | 'express'
    | 'cinematic_longform'
  visualOutputMode:
    | 'animated_scenes'
    | 'static_images'
    | 'mixed'
}

export const EMPTY_CREATE_CONTENT_DIRECTION: CreateContentDirection = {
  contentGoal: '',
  publishTarget: '',
  durationMode: '',
  durationTargetSec: undefined,
  productionMode: undefined,
  visualOutputMode: undefined,
  useCharacter: '',
  characterType: '',
  hasProductImage: '',
}

export const EMPTY_CREATE_CONTENT_DISCOVERY: CreateContentDiscovery = {
  topic: '',
  objective: '',
  audience: '',
  desiredAction: '',
  mustInclude: '',
  mustAvoid: '',
}

export function normalizeCreateContentDirection(
  value?: Partial<CreateContentDirection> | null
): CreateContentDirection {
  return {
    ...EMPTY_CREATE_CONTENT_DIRECTION,
    ...(value || {}),
  }
}

export function normalizeCreateContentDiscovery(
  value?: Partial<CreateContentDiscovery> | null
): CreateContentDiscovery {
  return {
    ...EMPTY_CREATE_CONTENT_DISCOVERY,
    ...(value || {}),
  }
}

export function shouldAskCreateContentCharacter(
  direction: CreateContentDirection
) {
  if (!direction.contentGoal) {
    return false
  }

  if (direction.contentGoal === 'ugc') {
    return false
  }

  return [
    'product',
    'storytelling',
    'viral',
    'auto',
  ].includes(direction.contentGoal)
}

export function getCreateContentDirectionStep(
  direction: CreateContentDirection,
  discovery?: CreateContentDiscovery
): CreateContentDirectionStep {
  if (!direction.contentGoal) {
    return 'content_goal'
  }

  if (!direction.publishTarget) {
    return 'publish_target'
  }

  if (!direction.durationMode) {
    return 'duration'
  }

  if (!direction.hasProductImage) {
    return 'product_image'
  }

  const shouldAskCharacter =
    shouldAskCreateContentCharacter(direction)

  if (
    shouldAskCharacter &&
    !direction.useCharacter
  ) {
    return 'character_presence'
  }

  if (
    shouldAskCharacter &&
    direction.useCharacter === 'yes' &&
    !direction.characterType
  ) {
    return 'character_type'
  }

  const currentDiscovery =
    discovery ||
    EMPTY_CREATE_CONTENT_DISCOVERY

  if (!currentDiscovery.topic.trim()) {
    return 'discovery_topic'
  }

  if (!currentDiscovery.objective) {
    return 'discovery_objective'
  }

  if (!currentDiscovery.audience.trim()) {
    return 'discovery_audience'
  }

  if (!currentDiscovery.desiredAction.trim()) {
    return 'discovery_action'
  }

  return 'completed'
}

export function getCreateContentDurationOptions(
  publishTarget: CreateContentDirection['publishTarget']
): DurationOption[] {
  if (publishTarget === 'shorts') {
    return [
      {
        id: 'short',
        label: 'Short viral',
        hint: '30–60 segundos',
        durationTargetSec: 60,
        productionMode: 'express',
        visualOutputMode: 'animated_scenes',
      },
      {
        id: 'medium',
        label: 'Short extendido',
        hint: '60–90 segundos',
        durationTargetSec: 90,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
      {
        id: 'auto',
        label: 'Que Samantha decida',
        hint: 'Optimizar según la idea',
        durationTargetSec: 60,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
    ]
  }

  if (publishTarget === 'youtube') {
    return [
      {
        id: 'short',
        label: 'Video corto',
        hint: '1–3 minutos',
        durationTargetSec: 180,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
      {
        id: 'medium',
        label: 'Video medio',
        hint: '4–8 minutos',
        durationTargetSec: 480,
        productionMode: 'cinematic_longform',
        visualOutputMode: 'static_images',
      },
      {
        id: 'long',
        label: 'YouTube largo',
        hint: '8–12 minutos',
        durationTargetSec: 720,
        productionMode: 'cinematic_longform',
        visualOutputMode: 'static_images',
      },
      {
        id: 'documentary',
        label: 'Mini documental',
        hint: '15–20 minutos',
        durationTargetSec: 1200,
        productionMode: 'cinematic_longform',
        visualOutputMode: 'static_images',
      },
      {
        id: 'auto',
        label: 'Que Samantha decida',
        hint: 'Según objetivo y tema',
        durationTargetSec: 600,
        productionMode: 'cinematic_longform',
        visualOutputMode: 'static_images',
      },
    ]
  }

  if (publishTarget === 'feed') {
    return [
      {
        id: 'short',
        label: 'Post breve',
        hint: '30–60 segundos',
        durationTargetSec: 60,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
      {
        id: 'medium',
        label: 'Contenido educativo',
        hint: '1–3 minutos',
        durationTargetSec: 180,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
      {
        id: 'auto',
        label: 'Que Samantha decida',
        hint: 'Optimizar según plataforma',
        durationTargetSec: 90,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
    ]
  }

  return [
    {
      id: 'auto',
      label: 'Que Samantha decida',
      hint: 'Recomendado',
      durationTargetSec: 60,
      productionMode: 'express',
      visualOutputMode: 'mixed',
    },
    {
      id: 'short',
      label: 'Corto',
      hint: '30–90 segundos',
      durationTargetSec: 60,
      productionMode: 'express',
      visualOutputMode: 'mixed',
    },
    {
      id: 'medium',
      label: 'Medio',
      hint: '2–5 minutos',
      durationTargetSec: 300,
      productionMode: 'express',
      visualOutputMode: 'mixed',
    },
    {
      id: 'long',
      label: 'Largo',
      hint: '8–12 minutos',
      durationTargetSec: 720,
      productionMode: 'cinematic_longform',
      visualOutputMode: 'static_images',
    },
  ]
}

function buildButtonValue(
  step: CreateContentDirectionStep,
  value: string
) {
  return `create_content_direction:${step}:${value}`
}

export function getCreateContentDirectionQuestion(
  direction: CreateContentDirection,
  discovery: CreateContentDiscovery
): CreateContentDirectionQuestion | null {
  const step = getCreateContentDirectionStep(direction, discovery)

  if (step === 'content_goal') {
    return {
      content:
        'Perfecto. Primero vamos a definir la dirección creativa.\n\n¿Qué tipo de pieza quieres crear?',
      buttons: [
        {
          label: 'Viral',
          value: buildButtonValue(step, 'viral'),
          emoji: '🔥',
        },
        {
          label: 'Producto',
          value: buildButtonValue(step, 'product'),
          emoji: '🛍️',
        },
        {
          label: 'Storytelling',
          value: buildButtonValue(step, 'storytelling'),
          emoji: '🎬',
        },
        {
          label: 'UGC',
          value: buildButtonValue(step, 'ugc'),
          emoji: '📱',
        },
        {
          label: 'Que Samantha elija',
          value: buildButtonValue(step, 'auto'),
          emoji: '✨',
        },
      ],
    }
  }

  if (step === 'publish_target') {
    return {
      content:
        '¿Dónde publicarás primero esta pieza?\n\nUsaré esto para optimizar el formato, encuadre y ritmo.',
      buttons: [
        {
          label: 'Reels / TikTok / Shorts',
          value: buildButtonValue(step, 'shorts'),
          emoji: '📱',
        },
        {
          label: 'Feed',
          value: buildButtonValue(step, 'feed'),
          emoji: '🖼️',
        },
        {
          label: 'YouTube / Web',
          value: buildButtonValue(step, 'youtube'),
          emoji: '▶️',
        },
        {
          label: 'Que Samantha decida',
          value: buildButtonValue(step, 'auto'),
          emoji: '✨',
        },
      ],
    }
  }

  if (step === 'duration') {
    const options =
      getCreateContentDurationOptions(
        direction.publishTarget
      )

    return {
      content:
        '¿Qué duración tendrá esta pieza?\n\nEsto define si conviene una producción express o una estructura más cinematográfica.',
      buttons: options.map((option) => ({
        label: `${option.label} · ${option.hint}`,
        value: buildButtonValue(
          step,
          option.id
        ),
      })),
    }
  }

  if (step === 'product_image') {
    return {
      content:
        '¿Quieres incluir una imagen real de producto?\n\nSi ya la tienes, después podremos usarla como referencia visual.',
      buttons: [
        {
          label: 'Sí, ya la tengo',
          value: buildButtonValue(step, 'yes'),
          emoji: '📦',
        },
        {
          label: 'Aún no',
          value: buildButtonValue(step, 'no'),
          emoji: '➡️',
        },
      ],
    }
  }

  if (step === 'character_presence') {
    return {
      content:
        '¿Cómo quieres manejar la presencia humana en esta pieza?',
      buttons: [
        {
          label: 'Usar personaje',
          value: buildButtonValue(step, 'yes'),
          emoji: '👤',
        },
        {
          label: 'Solo producto / visuales',
          value: buildButtonValue(step, 'no'),
          emoji: '🎨',
        },
        {
          label: 'Que Samantha recomiende',
          value: buildButtonValue(step, 'auto'),
          emoji: '✨',
        },
      ],
    }
  }

  if (step === 'character_type') {
    return {
      content:
        '¿Qué tipo de personaje quieres utilizar?',
      buttons: [
        {
          label: 'Experto / guía',
          value: buildButtonValue(step, 'expert'),
          emoji: '🎓',
        },
        {
          label: 'Creador / storyteller',
          value: buildButtonValue(step, 'creator'),
          emoji: '🎥',
        },
        {
          label: 'Fundador / marca personal',
          value: buildButtonValue(step, 'founder'),
          emoji: '🚀',
        },
        {
          label: 'Cliente / testimonio',
          value: buildButtonValue(step, 'customer'),
          emoji: '💬',
        },
        {
          label: 'Narrador',
          value: buildButtonValue(step, 'narrator'),
          emoji: '🎙️',
        },
        {
          label: 'Avatar IA',
          value: buildButtonValue(step, 'avatar'),
          emoji: '🤖',
        },
      ],
    }
  }

  if (step === 'discovery_topic') {
    return {
      content:
        'Ahora cuéntame de qué quieres que trate el contenido.\n\nPuedes escribir el tema, producto, nicho o idea principal como se lo explicarías a una persona.',
      buttons: [],
    }
  }

  if (step === 'discovery_objective') {
    return {
      content:
        '¿Cuál es el objetivo principal de esta pieza?',
      buttons: [
        {
          label: 'Vistas',
          value: buildButtonValue(
            step,
            'vistas'
          ),
          emoji: '👀',
        },
        {
          label: 'Leads',
          value: buildButtonValue(
            step,
            'leads'
          ),
          emoji: '🧲',
        },
        {
          label: 'Ventas',
          value: buildButtonValue(
            step,
            'ventas'
          ),
          emoji: '💰',
        },
        {
          label: 'Autoridad',
          value: buildButtonValue(
            step,
            'autoridad'
          ),
          emoji: '🎓',
        },
        {
          label: 'Comunidad',
          value: buildButtonValue(
            step,
            'comunidad'
          ),
          emoji: '🤝',
        },
      ],
    }
  }

  if (step === 'discovery_audience') {
    return {
      content:
        '¿A qué tipo de personas quieres llegar?\n\nDescribe la audiencia de forma sencilla. Por ejemplo: emprendedores que quieren usar IA para crear contenido.',
      buttons: [],
    }
  }

  if (step === 'discovery_action') {
    return {
      content:
        '¿Qué quieres que hagan al terminar de ver la pieza?\n\nPor ejemplo: seguirte, comentar una palabra, registrarse, comprar o pedir información.',
      buttons: [],
    }
  }

  return null
}

export function applyCreateContentDiscoveryAnswer(params: {
  discovery: CreateContentDiscovery
  step: CreateContentDirectionStep
  value: string
}): CreateContentDiscovery {
  const {
    discovery,
    step,
    value,
  } = params

  const cleanValue =
    String(value || '').trim()

  if (step === 'discovery_topic') {
    return {
      ...discovery,
      topic: cleanValue,
    }
  }

  if (step === 'discovery_objective') {
    return {
      ...discovery,
      objective:
        cleanValue as CreateContentDiscovery['objective'],
    }
  }

  if (step === 'discovery_audience') {
    return {
      ...discovery,
      audience: cleanValue,
    }
  }

  if (step === 'discovery_action') {
    return {
      ...discovery,
      desiredAction: cleanValue,
    }
  }

  return discovery
}

export function applyCreateContentDirectionAnswer(params: {
  direction: CreateContentDirection
  step: CreateContentDirectionStep
  value: string
}): CreateContentDirection {
  const {
    direction,
    step,
    value,
  } = params

  if (step === 'content_goal') {
    const contentGoal =
      value as CreateContentDirection['contentGoal']

    return {
      ...direction,
      contentGoal,

      useCharacter:
        contentGoal === 'ugc'
          ? 'yes'
          : contentGoal === 'auto'
            ? 'auto'
            : '',

      characterType:
        contentGoal === 'ugc'
          ? 'expert'
          : contentGoal === 'auto'
            ? 'auto'
            : '',
    }
  }

  if (step === 'publish_target') {
    return {
      ...direction,
      publishTarget:
        value as CreateContentDirection['publishTarget'],

      durationMode: '',
      durationTargetSec: undefined,
      productionMode: undefined,
      visualOutputMode: undefined,
    }
  }

  if (step === 'duration') {
    const selected =
      getCreateContentDurationOptions(
        direction.publishTarget
      ).find((option) => option.id === value)

    if (!selected) {
      return direction
    }

    return {
      ...direction,
      durationMode: selected.id,
      durationTargetSec:
        selected.durationTargetSec,
      productionMode:
        selected.productionMode,
      visualOutputMode:
        selected.visualOutputMode,
    }
  }

  if (step === 'product_image') {
    return {
      ...direction,
      hasProductImage:
        value as CreateContentDirection['hasProductImage'],
    }
  }

  if (step === 'character_presence') {
    const useCharacter =
      value as CreateContentDirection['useCharacter']

    return {
      ...direction,
      useCharacter,

      characterType:
        useCharacter === 'no'
          ? 'none'
          : useCharacter === 'auto'
            ? 'auto'
            : '',
    }
  }

  if (step === 'character_type') {
    return {
      ...direction,
      characterType:
        value as CreateContentDirection['characterType'],
    }
  }

  return direction
}

export function parseCreateContentDirectionButton(
  rawValue: string
): {
  step: CreateContentDirectionStep
  value: string
} | null {
  const value = String(rawValue || '').trim()

  if (
    !value.startsWith(
      'create_content_direction:'
    )
  ) {
    return null
  }

  const [
    ,
    rawStep,
    ...rest
  ] = value.split(':')

  const answer = rest.join(':').trim()

  const allowedSteps: CreateContentDirectionStep[] = [
    'content_goal',
    'publish_target',
    'duration',
    'product_image',
    'character_presence',
    'character_type',
    'discovery_objective',
  ]

  if (
    !allowedSteps.includes(
      rawStep as CreateContentDirectionStep
    ) ||
    !answer
  ) {
    return null
  }

  return {
    step:
      rawStep as CreateContentDirectionStep,
    value: answer,
  }
}

export function hasCreateContentDiscoveryContext(
  request: TaskFlowRequest
) {
  const payload = request?.payload || {}

  const discovery =
    payload?.discovery ||
    payload?.creativeDirection?.discovery ||
    {}

  const topic = String(
    discovery?.topic ||
      payload?.topic ||
      payload?.niche ||
      payload?.idea ||
      payload?.creativeDirection?.topic ||
      payload?.creativeDirection?.niche ||
      ''
  ).trim()

  const audience = String(
    discovery?.audience ||
      payload?.audience ||
      payload?.creativeDirection?.audience ||
      ''
  ).trim()

  return Boolean(topic && audience)
}